import {
    EuiBadge,
    EuiButton,
    EuiFormLabel,
    EuiSwitch,
    useEuiTheme,
} from '@elastic/eui'
import { Interpolation, Theme, css } from '@emotion/react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Table, TableColumn } from '@components/table'
import {
    useDeleteUserMutation,
    useFetchTenantsQuery,
    useFetchUsersQuery,
    useReactivateUserMutation,
} from '@services/api'
import { useEffect, useState } from 'react'
import { useHasDependentPermissions, useHasPermission } from '@hooks/auth'

import { Page } from '@components/layout'
import { Permissions } from '@services/auth'
import { Role } from '@services/roles'
import { User } from '@services/users'
import { useQuery } from '@hooks/query'
import { useTranslation } from 'react-i18next'

const UsersPage = () => {
    const { t } = useTranslation(['common', 'users'])
    const navigate = useNavigate()
    const { colors } = useEuiTheme().euiTheme

    const [includeSoftDelete, setIncludeSoftDelete] = useState<boolean>(false)

    const {
        query,
        handleQueryChange,
        handleQueryFiltersChange,
        clearQueryFilters,
    } = useQuery()

    const canCreateUser = useHasDependentPermissions('user', [
        'canView',
        'canCreate',
    ])
    const canUpdateUser = useHasDependentPermissions('user', [
        'canView',
        'canUpdate',
    ])
    const canDeleteUser = useHasDependentPermissions('user', ['canDelete'])

    const canViewTenants = useHasPermission(Permissions.tenant.canView)
    const canViewRoles = useHasPermission(Permissions.role.canView)

    const { data: tenants } = useFetchTenantsQuery(
        {},
        { skip: !canViewTenants }
    )
    const { data, isLoading } = useFetchUsersQuery(query)
    const [deleteUser, { isLoading: isDeleteLoading }] = useDeleteUserMutation()
    const [reactivateUser, { isLoading: isReactivateLoading }] =
        useReactivateUserMutation()

    const customButtons = [
        <EuiSwitch
            key="toggle-deleted-users-switch"
            compressed
            label={<EuiFormLabel>{t('users:show_deleted_users')}</EuiFormLabel>}
            checked={includeSoftDelete}
            onChange={(e) => setIncludeSoftDelete(e.target.checked)}
        />,
    ]

    const columns: TableColumn[] = [
        {
            field: 'userName',
            name: t('users:userName'),
            sortable: true,
            filterable: true,
            filterType: 'text',
            filterPlaceholder: t('users:filter_by_username'),
        },
        {
            field: 'firstName',
            name: t('users:firstName'),
            sortable: true,
            filterable: true,
            filterType: 'text',
            filterPlaceholder: t('users:filter_by_firstName'),
        },
        {
            field: 'lastName',
            name: t('users:lastName'),
            sortable: true,
            filterable: true,
            filterType: 'text',
            filterPlaceholder: t('users:filter_by_lastName'),
        },
        {
            field: 'email',
            name: t('users:email'),
            sortable: true,
            filterable: true,
            filterType: 'text',
            filterPlaceholder: t('users:filter_by_email'),
        },
        ...(canViewRoles
            ? [
                  {
                      field: 'roles',
                      name: t('users:roles'),
                      sortable: true,
                      filterable: true,
                      formatter: (roles: Role[]) => {
                          return (
                              <span>
                                  {roles.map((r) => (
                                      <EuiBadge key={r.id}>{r.name}</EuiBadge>
                                  ))}
                              </span>
                          )
                      },
                      // filterType: 'tags',
                      // filterPlaceholder: t('users:filter_by_email'),
                  } as TableColumn,
              ]
            : []),
        {
            field: 'isEnabled',
            name: t('users:is_enabled'),
            renderAs: 'boolean',
            sortable: true,
            filterable: true,
            filterType: 'boolean',
            filterPlaceholder: t('users:filter_by_status'),
            filterOptions: [
                {
                    value: 'true',
                    label: t('common:active'),
                },
                {
                    value: 'false',
                    label: t('common:inactive'),
                },
            ],
        },
        ...(canViewTenants
            ? [
                  {
                      field: 'tenant.name',
                      name: t('users:tenant'),
                      sortable: true,
                      filterable: true,
                      filterField: 'tenantId',
                      filterType: 'options',
                      filterPlaceholder: t('users:filter_by_tenant'),
                      filterOptions:
                          tenants?.items?.map((item) => ({
                              label: item.name,
                              value: item.id,
                          })) ?? [],
                  } as TableColumn,
              ]
            : []),
        {
            field: 'action',
            renderAs: 'action',
            onView: {
                onClick: (user: User) => navigate(`/users/${user.id}`),
                available: (user: User) => !canUpdateUser && !user.isDeleted,
            },
            onEdit: {
                onClick: (user: User) => navigate(`/users/${user.id}`),
                available: (user: User) => canUpdateUser && !user.isDeleted,
            },
            onDelete: {
                onClick: (user: User) => deleteUser(user.id),
                available: (user: User) => canDeleteUser && !user.isDeleted,
            },
            customActions: [
                {
                    label: t('users:reactivate'),
                    description: t('users:reactivate'),
                    icon: 'refresh',
                    enabled: (user: User) => user.isDeleted && canUpdateUser,
                    available: (user: User) => user.isDeleted,
                    onClick: (user: User) => reactivateUser(user.id),
                },
            ],
        },
    ]

    const handleRowCss = (user: User): Interpolation<Theme> => {
        if (!user.isDeleted) return undefined

        return css`
            background-color: ${colors.backgroundBaseSubdued};

            span:not(
                    .ignore-color-css.euiBadge,
                    .ignore-color-css .euiBadge__content,
                    .ignore-color-css .euiBadge__text
                ) {
                color: ${colors.textDisabled} !important;
            }
        `
    }

    useEffect(() => {
        handleQueryFiltersChange([`includeSoftDelete==${includeSoftDelete}`])
    }, [includeSoftDelete])

    return (
        <>
            <Outlet />
            <Page
                title={t('users:title')}
                iconType="securityApp"
                items={
                    canCreateUser
                        ? [
                              <EuiButton
                                  key="btn_new_user"
                                  color="primary"
                                  iconType="plus"
                                  onClick={() => navigate('/users/new')}
                              >
                                  {t('users:new_user')}
                              </EuiButton>,
                          ]
                        : []
                }
            >
                <Table<User>
                    loading={
                        isLoading || isDeleteLoading || isReactivateLoading
                    }
                    items={data?.items ?? []}
                    totalCount={data?.totalCount ?? 0}
                    columns={columns}
                    customButtons={customButtons}
                    onFilter={handleQueryFiltersChange}
                    onFilterClear={clearQueryFilters}
                    onChange={handleQueryChange}
                    rowCss={handleRowCss}
                />
            </Page>
        </>
    )
}

export default UsersPage
